import {
  Button,
  Box,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useCallback, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF } from '@tonic-one/react-data-grid';

const draggingColor: any = {
  dark: '#424242',
  light: '#c9c9c9',
};

const notDraggingColor: any = {
  dark: '#212121',
  light: '#f2f2f2',
};

interface CecpColumnConfig {
  order: string[];
  hideIds: string[];
  columnNames: { [columnId: string]: string };
}

export default function ManageColumnsModal(props: {
  visible: boolean;
  columnConfig: CecpColumnConfig;
  onSave: (columnConfig: CecpColumnConfig) => void;
  onClose: () => void;
}) {
  const { visible, onClose, columnConfig, onSave: _onSave } = props;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [newColumnOrder, setNewColumnOrder] = useState(columnConfig.order);
  const [newColumnHideIds, setNewColumnHideIds] = useState(
    columnConfig.hideIds
  );
  const grid = 8;

  const reorder = useCallback(
    (list: any[], startIndex: number, endIndex: number) => {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      return result;
    },
    []
  );

  const onDragEnd = useCallback(
    (result: any) => {
      if (!result.destination) {
        return;
      }

      const reorderedItems = reorder(
        newColumnOrder,
        result.source.index,
        result.destination.index
      );

      setNewColumnOrder(reorderedItems);
    },
    [newColumnOrder, reorder]
  );

  const itemToTop = useCallback(
    (index: number) => {
      const result = Array.from(newColumnOrder);
      const [removed] = result.splice(index, 1);
      setNewColumnOrder([removed, ...result]);
    },
    [newColumnOrder]
  );

  const onSave = useCallback(() => {
    _onSave({
      order: newColumnOrder,
      hideIds: newColumnHideIds,
      columnNames: columnConfig.columnNames,
    });
    onClose();
  }, [
    _onSave,
    newColumnOrder,
    newColumnHideIds,
    columnConfig.columnNames,
    onClose,
  ]);

  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={visible}
      onClose={onClose}
      size="sm"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent backgroundColor={colorStyle.background.secondary}>
        <ModalHeader>{t('log.modals.manageColumns.title')}</ModalHeader>
        <Text pb="16px" pl="24px">
          {t('log.modals.manageColumns.desc')}
        </Text>
        <ModalBody py="0px" px="18px">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <Box
                  width="100%"
                  height="300px"
                  cursor="move"
                  padding={grid}
                  overflow="auto"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {newColumnOrder.map((item: any, index: any) => {
                    if (
                      [
                        DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF.id,
                        'actions',
                        'mitigation_status',
                      ].includes(item)
                    ) {
                      return null;
                    }
                    return (
                      <Draggable key={item} draggableId={item} index={index}>
                        {(provided, snapshot) => (
                          <Box
                            margin="0"
                            height="36px"
                            borderBottom="10px"
                            background={
                              snapshot.isDragging
                                ? draggingColor[colorMode]
                                : notDraggingColor[colorMode]
                            }
                            cursor="move"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={provided.draggableProps.style}
                          >
                            <DragItem
                              item={item}
                              columnNames={columnConfig.columnNames}
                              columnHideIds={newColumnHideIds}
                              setColumnHideIds={setNewColumnHideIds}
                              onToTop={() => {
                                itemToTop(index);
                              }}
                            />
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" minWidth="20x" onClick={onSave}>
            {t('common.action.label.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onCancel}>
            {t('common.action.label.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const DragItem = (props: {
  item: string;
  columnNames: { [columnId: string]: string };
  columnHideIds: string[];
  setColumnHideIds: (columnHideIds: string[]) => void;
  onToTop: () => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { item, onToTop, columnNames, columnHideIds, setColumnHideIds } = props;

  const onChange = (e: any) => {
    if (e.target.checked) {
      setColumnHideIds(columnHideIds.filter((id) => id !== item));
    } else {
      setColumnHideIds([...columnHideIds, item]);
    }
  };

  return (
    <Flex
      justifyContent="space-between"
      className="dragItem"
      _hover={{ backgroundColor: colorStyle.background.fourth }}
    >
      <Flex alignItems="center">
        <DragIcon />
        <Checkbox
          value={item}
          checked={!columnHideIds.includes(item)}
          onChange={onChange}
        >
          {columnNames[item]}
        </Checkbox>
      </Flex>
      <Button
        variant="ghost"
        size="sm"
        onClick={onToTop}
        px="0px"
        color={colorStyle.color.primary}
        className="toTopBtn"
      >
        <GotoTopIcon />
      </Button>
    </Flex>
  );
};

function GotoTopIcon() {
  return (
    <svg
      width="12"
      height="14"
      viewBox="0 0 12 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.3726 0.663012C11.3726 0.838983 11.3027 1.00775 11.1783 1.13218C11.0539 1.25661 10.8851 1.32651 10.7091 1.32651H1.17837C1.0024 1.32651 0.833639 1.25661 0.709209 1.13218C0.584778 1.00775 0.514874 0.838983 0.514874 0.663012C0.514874 0.487041 0.584778 0.318277 0.709209 0.193846C0.833639 0.069416 1.0024 -0.000488281 1.17837 -0.000488281H10.7091C10.8851 -0.000488281 11.0539 0.069416 11.1783 0.193846C11.3027 0.318277 11.3726 0.487041 11.3726 0.663012ZM5.99687 3.13051C5.8209 3.13051 5.65214 3.20042 5.52771 3.32485C5.40328 3.44928 5.33337 3.61804 5.33337 3.79401V13.3248C5.33337 13.5007 5.40328 13.6695 5.52771 13.7939C5.65214 13.9184 5.8209 13.9883 5.99687 13.9883C6.17285 13.9883 6.34161 13.9184 6.46604 13.7939C6.59047 13.6695 6.66037 13.5007 6.66037 13.3248V3.79401C6.66037 3.61804 6.59047 3.44928 6.46604 3.32485C6.34161 3.20042 6.17285 3.13051 5.99687 3.13051ZM6.46787 2.64101C6.20862 2.38201 5.80337 2.36701 5.56287 2.60776L0.675124 7.49501C0.434374 7.73576 0.449374 8.14101 0.708374 8.40001C0.967624 8.65926 1.37262 8.67401 1.61337 8.43326L6.50087 3.54601C6.74137 3.30526 6.72662 2.90001 6.46762 2.64101H6.46787ZM5.52612 2.64101C5.26712 2.90001 5.25212 3.30526 5.49287 3.54601L10.3804 8.43351C10.6209 8.67401 11.0261 8.65926 11.2854 8.40001C11.5444 8.14101 11.5591 7.73576 11.3184 7.49501L6.43112 2.60776C6.19037 2.36701 5.78512 2.38201 5.52612 2.64101Z"
        fill="currentColor"
        fillOpacity="0.92"
      />
    </svg>
  );
}

function DragIcon() {
  return (
    <svg
      className="dragIcon"
      width="16"
      height="16"
      viewBox="0 0 8 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="2" cy="2" r="1" fill="currentColor" />
      <circle cx="2" cy="6" r="1" fill="currentColor" />
      <circle cx="2" cy="10" r="1" fill="currentColor" />
      <circle cx="2" cy="14" r="1" fill="currentColor" />
      <circle cx="6" cy="2" r="1" fill="currentColor" />
      <circle cx="6" cy="6" r="1" fill="currentColor" />
      <circle cx="6" cy="10" r="1" fill="currentColor" />
      <circle cx="6" cy="14" r="1" fill="currentColor" />
    </svg>
  );
}
